import React, { useEffect, useState } from 'react';
import { AddTwoToneIcon, Anchor, Button, Chip, KebabMenu, PageHeader, Pager, SearchBar, Table, TableColumn, TableRow } from '@keplerco/core';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { SkeletonLoader } from '../../../components/general/loading-state/loaders/skeleton-loader/skeleton-loader';
import { PagerConnector } from '../../../components/general/pager-connector/pager-connector';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';
import { PagePath } from '../../../navigation/navigation.enums';
import { useAppActions, useAppState } from '../../../overmind';
import { CompanyRoleListResponse } from '../../../models/overmind/company-entity';
import roleSkillManagementStyles from '../role-skill-management.module.css';
import classNames from 'classnames';
import { SearchRolesRequest } from '../../../models/overmind/search-params';

export function ManageRolesPage(): JSX.Element {
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [loadingRoles, setLoadingRoles] = useState<string[]>([]);
  const [request, setRequest] = useState<SearchRolesRequest>({
    search: undefined,
    sortAscending: true,
    pageSize: 10,
    page: 1,
    companySlug: companyVariables.slug,
    searchGlobally: false,
  });
  const [data, setData] = useState<CompanyRoleListResponse>();

  async function getData(request: SearchRolesRequest) {
    const response = await actions.getCompanyRoles(request);
    setData(response);
  }

  useEffect(() => {
    async function initPage() {
      setIsLoading(true);
      await getData(request);
      setIsLoading(false);
    }

    initPage();
  }, [companyVariables.slug]);

  return (
    <div className="wrapper stack" style={{ gap: 24 }}>
      <PageHeader
        breadcrumbs={[
          { name: 'Role & Skill Management', url: PagePath.roleSkillManagementBase },
          { name: 'Manage roles', url: window.location.pathname },
        ]}
        title="Manage roles"
        subtitle="A library of all the roles within your company"
      />

      <div className={roleSkillManagementStyles.actions}>
        <SearchBar
          loading={isSearching}
          label="Search"
          onInput={async event => {
            setIsSearching(true);
            const search = (event.target as HTMLInputElement).value;
            const nextRequest = { ...request, search, page: 1 };
            setRequest(nextRequest);
            await getData(nextRequest);
            setIsSearching(false);
          }}
        />

        <Button fullWidthMobile type="button" square theme="dark" onClick={() => keplerNavigate(`${PagePath.roleSkillManagementBase}${PagePath.roleSkillManagementWizard.replace(':companySlug', companyVariables.slug!)}`)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <AddTwoToneIcon />
            Create role
          </div>
        </Button>
      </div>

      {isLoading ? (
        <SkeletonLoader height="500px" />
      ) : !data?.roles.length ? (
        <EmptyState />
      ) : (
        <Table
          standAloneConfigColumn
          currentSortBy="role"
          currentSortDirection={request.sortAscending ? 'Ascending' : 'Descending'}
          onSort={() => setRequest(currentState => ({ ...currentState, sortAscending: !currentState.sortAscending }))}
          footerContent={
            <div className={classNames({ invisible: data.totalPages <= 1 })}>
              <PagerConnector
                defaultPageNumber={request.page}
                pageCount={data?.totalPages ?? 0}
                onPageChange={async page => {
                  setIsLoading(true);
                  const nextRequest = { ...request, page };
                  setRequest(nextRequest);
                  await getData(nextRequest);
                  setIsLoading(false);
                }}
              >
                {connector => <Pager {...connector} isLoading={isLoading} />}
              </PagerConnector>
            </div>
          }
        >
          {data?.roles.map(role => {
            return (
              <TableRow
                isLoading={loadingRoles.includes(role.slug!)}
                key={role.slug}
                id={role.slug!}
                configCell={() => (
                  <KebabMenu
                    items={[
                      {
                        label: 'Activate Role',
                        onClick: async () => {
                          if (!role.slug) return;
                          setLoadingRoles(currentState => [...currentState, role.slug!]);

                          await actions.activateCompanyRole({
                            companySlug: companyVariables.slug!,
                            roleSlug: role.slug,
                            keplerNavigate,
                          });
                          await getData(request);

                          setLoadingRoles([]);
                        },
                        disabled: role.active,
                      },
                      {
                        label: 'Edit Role',
                        onClick: () => keplerNavigate(`${PagePath.roleSkillManagementBase}${PagePath.roleSkillManagementWizardUpdate.replace(':companySlug', companyVariables.slug!).replace(':roleSlug', role.slug!)}`),
                      },
                    ]}
                  />
                )}
              >
                <TableColumn sortable id="role" label="Role">
                  <Anchor hovertype="opacity" textTransform="none" onClick={() => keplerNavigate(`${PagePath.roleSkillManagementBase}${PagePath.roleSkillManagementWizardUpdate.replace(':companySlug', companyVariables.slug!).replace(':roleSlug', role.slug!)}`)}>
                    {role.name}
                  </Anchor>
                </TableColumn>

                <TableColumn id="status" label="Status">
                  {role.active ? <Chip themeColour="g">Active</Chip> : <Chip customTheme={{ backgroundColor: 'background', borderColor: 'borders', color: 'text' }}>Draft</Chip>}
                </TableColumn>

                <TableColumn id="description" label="Description">
                  <div className={roleSkillManagementStyles.description}>{role.description}</div>
                </TableColumn>
              </TableRow>
            );
          })}
        </Table>
      )}
    </div>
  );
}
